import { SHOT_RATIO } from './shotShape';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Maximize2, Pause, Play, VideoOff, X } from 'lucide-react';
import { mountAssembly, type AssemblyView, type CamPose } from './assemblyScene';
import { modalCardCls, overlayCls } from './overlay';
import { camAt } from './assemblyStore';
import { ease } from './assemblyPose';
import { CLIP_MS, CLIP_SPEED, CLIP_STEP, clipAssembly, clipMs, clipScene, clipStatesAt, type Clip, type ClipAssembly, type statesAt } from './assemblyClip';
import { meshAtSrc } from './threeDApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { RingAndSpeed } from './RingTransport';
import { ReaderScale } from './ReaderScale';
import { nextSpeed } from './ScrubBar';
import { tip } from '../../publicRoute';

// A CLIP, PLAYING. The read-only end of Phase 5's viewer: the same meshes, the same movement
// and the same shot, with none of the answering — nothing here selects, drags, hides, measures
// or writes. It is what the fitting LOOKS like, for the two places that only want to look.
//
// Those two are the publish review, where an operator checks what a visitor will be shown, and
// the public page, where the visitor is shown it. They differ in where the meshes are read
// from and in nothing else, which is why they are one component: a review drawing the clip by
// its own rules would be a review of something almost like what gets published.
//
// PLAYING ON A LOOP AND NOT ON A SCRUBBER. The phase's transport exists to stop on the frame
// you are judging; here there is nothing to judge and nothing to type, so the whole control is
// play, pause and see-it-bigger. It restarts by itself, because a clip left on its last frame
// is a still picture of a finished assembly — the one moment of the movement that says nothing
// about how it was made.

// How long the loop rests on the finished assembly before running again, so the end reads as
// an end rather than as a stutter.
const HOLD_MS = 900;

// The two end buttons of the big view's transport, in the public page's blue.
const publicEndBtn = 'shrink-0 rounded-md border border-notice-blue/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-notice-blue transition-colors hover:bg-notice-blue/5';

// The shape of every shot (see shotShape), re-exported for the players that draw one.
export { SHOT_ASPECT } from './shotShape';
// AND THE READER'S DRESS, worn by Phase 6's viewport too: the published player's ground and edge,
// and its light, half again the scene's own (a dark wood frame is nearly black on a phone without
// it). One of each, so the picture framed is the picture published (Louis 2026-10-28).
export const READER_FRAME = 'rounded-2xl border border-notice-blue/15 bg-notice-cream';
export const READER_BRIGHTNESS = 1.5;

// How long a clip following another takes to fly the camera from that one's last shot to its own.
// At ×1, and shorter in proportion at a higher speed (see enterMs).
export const ENTER_MS = 1500;
export const enterMs = (speed: number) => ENTER_MS / speed;
// A CAMERA BETWEEN TWO: what it looks at slid across, and where it stands swung round that, the
// distance eased from one to the other, so it orbits rather than cutting through the parts.
const poseBetween = (a: CamPose, b: CamPose, k: number): CamPose => {
  const mix = (p: number[], q: number[]) => p.map((v, i) => v + (q[i] - v) * k) as [number, number, number];
  const target = mix(a.target, b.target);
  const off = (p: CamPose) => p.eye.map((v, i) => v - p.target[i]);
  const [oa, ob] = [off(a), off(b)];
  const len = (v: number[]) => Math.hypot(...v) || 1;
  const dir = mix(oa.map((v) => v / len(oa)), ob.map((v) => v / len(ob)));
  const dist = len(oa) + (len(ob) - len(oa)) * k;
  const eye = dir.map((v, i) => target[i] + (v / len(dir)) * dist) as [number, number, number];
  const up = mix(a.up, b.up);
  return { eye, target, up: up.map((v) => v / len(up)) as [number, number, number] };
};

// EACH PART AT THE STATE ITS SCREWS HAVE REACHED (see statesAt), told to the view only when that
// changes: hiding redraws and re-measures, which sixty times a second is for nothing.
const statesShown = new WeakMap<AssemblyView, string>();
export function showStates(view: AssemblyView, states: ReturnType<typeof statesAt>) {
  if (!states) return;
  const key = JSON.stringify(states);
  if (statesShown.get(view) === key) return;
  statesShown.set(view, key);
  view.hide(states.hide);
  states.pieces.forEach(([id, names]) => view.hidePieces(id, names));
}

// ONE FRAME OF A CLIP: its parts at `t`, and its camera on its shot — or on its way in from
// another's (`enter`, `k` of the way), or left where the reader put it (`false`). The stage's loop
// and the video export draw by this alone, so a video is the picture played.
export function drawClipFrame(
  view: AssemblyView, asm: ClipAssembly, clip: Clip, t: number,
  enter: { from: CamPose; k: number } | null | false,
) {
  view.show(asm, CLIP_STEP, t);
  showStates(view, clipStatesAt(clip, asm, t));
  if (enter === false) return;
  view.look(camAt(clip.movement, t));
  if (enter && enter.k < 1) view.placeCamera(poseBetween(enter.from, view.cameraPose(), ease(enter.k)));
}

export function ClipStage({
  clip,
  meshUrl,
  playing,
  onState,
  onT,
  seek,
  once = false,
  onEnd,
  brightness = 1,
  speed = 1,
  enterFrom = null,
  onViewer,
  floorShadow = false,
  headroom,
  scrollsUpDown,
  sharedGl,
}: {
  clip: Clip;
  // One GPU context with the other views of this name (see AssemblyOpts.sharedGl).
  sharedGl?: string;
  // A SHADOW ON THE FLOOR under the product (see AssemblyView.floorShadow): the cover's.
  floorShadow?: boolean;
  // Room kept clear at the top of the shot (see AssemblyOpts.headroom).
  headroom?: number;
  // In a list that scrolls (see AssemblyOpts.scrollsUpDown).
  scrollsUpDown?: boolean;
  // THE VIEWER ITSELF, once built: for a caller carrying the camera from one clip into the next
  // (the assembly phase's "All").
  onViewer?: (view: AssemblyView | null) => void;
  // AND WHERE THE CAMERA COMES FROM, in this clip's frame: flown from there into the clip's own shot
  // over its opening (ENTER_MS), where it follows another clip (the assembly phase's "All", which
  // carries the last clip's camera across into this one's frame). Null: its own from the start.
  enterFrom?: CamPose | null;
  // HOW MUCH FASTER THAN THE CLIP'S OWN PACE: the assembly phase's "All" plays at ×2 too.
  speed?: number;
  // How bright the picture is, against the lighting's own 1 — see AssemblyView.brightness.
  brightness?: number;
  // How this reader addresses a mesh. Omitted, a clip reads through the signed-in app's own
  // route — see clipScene.
  meshUrl?: (job: number) => string;
  playing: boolean;
  onState?: (state: 'loading' | 'ready' | 'failed') => void;
  // WHERE THE CLIP IS, REPORTED — for a caller that draws a scrubber and has to keep its
  // handle under the picture. Only where one asks: this fires on every frame, and a caller
  // with no bar to move would be re-rendering to be told a number it does not print.
  onT?: (t: number) => void;
  // AND WHERE TO PUT IT, from the same caller's bar. A number, not a command: setting it to
  // where the clip already is does nothing, which is what makes it safe to pass on every
  // render of a scrubber being dragged.
  seek?: number | null;
  // STOP AT THE END INSTEAD OF STARTING AGAIN.
  //
  // A clip loops by default, and on the public page that is the point: a visitor is doing the
  // gesture while they watch it, and the thing they want is to see it again without asking. In
  // a REVIEW nobody is holding a screw — the loop is a picture that will not settle, and what
  // is being checked is what the gesture ends up as.
  once?: boolean;
  // Fired when a `once` clip reaches its last frame, so the caller can put its own transport
  // back to "play". The stage keeps drawing that frame; it just stops advancing.
  onEnd?: () => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<AssemblyView | null>(null);
  const [ready, setReady] = useState(false);
  // How many times the scene has handed its view over (see onReady).
  const [viewIn, setViewIn] = useState(0);
  // WHETHER THIS CARD HAS EVER BEEN ON SCREEN — see the observer below, and the effect after
  // it, which is what waits for this.
  const [seen, setSeen] = useState(false);
  // How far through the clip is, and which clip that is about — see the loop below, which is
  // the only thing that writes them.
  const where = useRef(0);
  const forClip = useRef<Clip['movement'] | null>(null);
  // WHETHER THE VISITOR HAS TAKEN THE CAMERA — turned, slid or zoomed the picture. From then on
  // the clip moves the parts and leaves the view alone: the shot is where to watch from until
  // somebody chooses for themselves, and a camera put back on every frame is a picture nobody
  // can look round while it plays. Theirs until the next clip, which opens on its own shot.
  const taken = useRef(false);
  const endRef = useRef(onEnd);
  endRef.current = onEnd;
  // HOW LONG IT HAS PLAYED SINCE IT WAS GIVEN WHERE ITS CAMERA COMES FROM (see enterFrom).
  const entered = useRef(0);
  useEffect(() => { entered.current = 0; }, [enterFrom]);
  const viewerRef = useRef(onViewer);
  viewerRef.current = onViewer;
  const speedRef = useRef(speed);
  speedRef.current = speed;
  // HOW MANY TIMES THE SCENE HAS BEEN BUILT AGAIN after the browser took its context back (see
  // onContextLost). Three, and then it stays blank: a browser taking every one is not a race.
  const [rebuilt, setRebuilt] = useState(0);

  // The scene is rebuilt only when the MESHES change. A clip's movement can be re-read — the
  // publish review rebuilds its snapshot as stores settle — without the parts changing, and a
  // remount there would restart a megabyte of downloads under a picture already playing.
  // And the state each is drawn at (see ClipPart.at): a part drilled since is a different mesh.
  // And the states it is drawn at before its screws are in (see ClipPart.driven).
  const at = (q: { job: number; at?: number; driven?: { at: number }[] }) => `${q.job}@${q.at ?? ''}${(q.driven ?? []).map((d) => `<${d.at}`).join('')}`;
  const meshes = clip.parts.map((p) => `${at(p)}:${(p.pieces ?? []).map(at).join('+')}`).join(',');

  // NOT UNTIL THE CARD IS LOOKED AT.
  //
  // A page of these mounts one scene per sub-step, and every one of them started downloading
  // its meshes on mount — fifteen clips pulling at once, over one connection, before anybody
  // has scrolled past the first two. The clips that lost that race are the heavy ones, which
  // is exactly backwards: a screw and a washer are a few hundred kilobytes and land at once,
  // while the sub-step holding the whole backrest frame and a side rail sat blank at the
  // bottom of the page waiting behind fourteen cards nobody was looking at.
  //
  // ONCE SEEN, ALWAYS MOUNTED. This is not a recycler — a card that scrolls back out keeps its
  // scene, because tearing it down would re-download a megabyte the next time it passes the
  // fold, and a page being scrolled up and down would do nothing but thrash. What it buys is
  // the ORDER things are asked for in, which is the whole of the problem.
  //
  // A GENEROUS MARGIN, so a card is built while it is still below the fold and is finished by
  // the time it arrives. The point is to stagger the downloads, not to make somebody watch a
  // loading frame they scrolled to.
  useEffect(() => {
    if (seen || !host.current) return;
    // No IntersectionObserver at all (an old browser, a test environment) is not a reason to
    // draw nothing: fall back to what this did before, which is mount immediately.
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setSeen(true); },
      { rootMargin: '600px' },
    );
    io.observe(host.current);
    return () => io.disconnect();
  }, [seen]);

  useEffect(() => {
    view.current = null;
    setReady(false);
    if (!seen || !host.current) return undefined;

    // AN EARLIER STATE OF A PART, in the app (see ClipPart.at); a published page draws it as it stands.
    return mountAssembly(host.current, clipScene(clip, meshUrl, meshUrl ? undefined : meshAtSrc), {
      gizmo: false,
      framedAt: SHOT_RATIO,
      headroom,
      scrollsUpDown,
      sharedGl,
      // The cover's floor re-aims the camera once the parts are in; its drag waits for that.
      heldUntilReleased: shadowWanted.current,
      onContextLost: () => setRebuilt((n) => (n < 3 ? n + 1 : n)),
      // Turned about the parts and not about where the shot aims: see AssemblyOpts.
      onCameraTaken: () => { taken.current = true; },
      // AND THE SHOT IS FRAMED ONLY ONCE THE VIEW IS IN HAND (`viewIn`, below): the scene says
      // "ready" a moment before it hands the view over, and a shot framed in between went to
      // no view at all. Three of a page's part cards were left with the camera where it is
      // made, inside the part, and showed nothing (Louis 2026-10-23).
      onReady: (v) => { view.current = v; setViewIn((n) => n + 1); viewerRef.current?.(v); },
      onState: (s) => {
        onState?.(s);
        setReady(s === 'ready');
      },
    });
    // `clip` deliberately absent: see `meshes`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meshes, meshUrl, seen, rebuilt, headroom, scrollsUpDown]);

  // The shot is framed ONCE, when the meshes land, and the phase's own furniture — the gizmo
  // (off from the mount), the measurement arrows, the origin crosses — is left off: they are for answering a fitting,
  // and this is the picture the answer produced. Re-framing per frame would push the camera in
  // as the part approached, and a part cannot be seen to travel if the world travels with it.
  useEffect(() => {
    if (!ready || !view.current) return;
    view.current.frame(clipAssembly(clip), CLIP_STEP);
    view.current?.measures([]);
    // The six face names go with them, and separately, now that they have their own switch:
    // the box is drawn while EITHER is on, so a player that only silenced the measurements
    // would show a wireframe crate with "Top" and "Front" floating in it.
    view.current?.faces([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, meshes, viewIn]);

  useEffect(() => {
    if (ready) view.current?.brightness(brightness);
  }, [ready, brightness, viewIn]);

  // Once the first frame has stood the parts where they go: the loop's effect below draws it,
  // and this one is declared after it so it runs after it.
  const shadowWanted = useRef(floorShadow);
  shadowWanted.current = floorShadow;


  // ONE LOOP DRIVING BOTH THE PARTS AND THE SHOT, and `t` never becomes state: a clip
  // re-rendering sixty times a second is a React tree per frame for a picture three.js draws
  // either way. The parent owns whether it runs; this owns where it is.
  // ONE PLACE THAT DRAWS A FRAME, because three things ask for one: the loop, the pause, and
  // a hand on the scrubber. `where` is written here so none of them has to remember to.
  const asm = useMemo(() => clipAssembly(clip, !meshUrl), [clip, meshUrl]);
  const drawAt = useCallback((t: number) => {
    where.current = t;
    // BY THE TIME IT HAS BEEN PLAYING, not by how far the clip is: the product assembled at the
    // end of "All" is a still a moment long, and its glide stopped at its first hundredth.
    const k = enterFrom ? Math.min(1, entered.current / enterMs(speedRef.current)) : 1;
    if (view.current) drawClipFrame(view.current, asm, clip, t, taken.current ? false : k < 1 ? { from: enterFrom!, k } : null);
    onT?.(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asm, clip, onT, enterFrom]);

  // A HAND ON THE BAR. Drawn straight rather than waiting for the loop, which is not running:
  // scrubbing pauses, and a frame that appeared only when play was pressed again would make
  // the bar look broken.
  useEffect(() => {
    if (!ready || seek == null) return;
    drawAt(seek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seek, ready]);

  useEffect(() => {
    if (!ready) return;
    const at = drawAt;
    // WHERE THE CLIP IS, ACROSS A PAUSE. `t` was a local of this effect and the effect re-runs
    // on every change of `playing` — so pausing threw the position away and drew t=0, and
    // pressing pause on a washer halfway down a screw put it back at the beginning. A ref,
    // because the loop writes it sixty times a second and none of those writes is a render.
    if (forClip.current !== clip.movement) {
      forClip.current = clip.movement;
      where.current = 0;
      taken.current = false;
    }
    at(where.current);
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    // Pressing play at the END starts it again, which is what the button means there —
    // otherwise it resumes on the last frame and holds.
    let t = where.current >= 1 ? 0 : where.current;
    let holding = 0;
    // Advanced against the wall clock rather than by counting frames, so a dropped frame costs
    // a frame and not a slower step.
    const tick = (now: number) => {
      const elapsed = now - last;
      last = now;
      if (holding > 0) {
        holding -= elapsed;
        if (holding <= 0) t = 0;
      } else {
        // NOTHING MOVES WHILE ITS CAMERA IS STILL ON ITS WAY IN (see enterFrom): at ×4 the washers
        // slid on before the camera had arrived.
        const arriving = !!enterFrom && entered.current < enterMs(speedRef.current);
        if (!arriving) t += (elapsed * speedRef.current) / (clipMs(clip.movement) / CLIP_SPEED);
        entered.current += elapsed;
        // PLAYED ONCE STOPS ON THE LAST FRAME. Drawn first and then left alone — the loop ends
        // and the scene goes on rendering that pose, which is what "stopped at the end" means
        // for something that has no frames of its own to hold.
        if (t >= 1 && once) { at(1); endRef.current?.(); return; }
        if (t >= 1) { t = 1; holding = HOLD_MS; }
      }
      at(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `once` and `onEnd` are read through a ref, so a caller re-rendering does not
    // restart the clip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `viewIn`: the shot itself is set here, and the first run may have come before the view did
    // (see onReady); the first three part cards were framed but seen from straight above.
  }, [ready, playing, meshes, clip.movement, viewIn]);

  useEffect(() => {
    if (ready && view.current && shadowWanted.current) {
      view.current.floorShadow();
      view.current.release();
    }
  }, [ready, meshes, viewIn]);

  return <div ref={host} className="h-full w-full" />;
}

// The clip in a frame: the picture, a play/pause, and the button that opens it big. `label`
// names the screen it belongs to ("1A"), for the modal's header.
export function ClipPlayer({
  clip,
  label,
  meshUrl,
  className,
  emptyClassName,
  autoPlay = true,
  once = false,
  transport = false,
  still = false,
  caption,
  noBigView = false,
  brightness,
  speeds,
  floorShadow,
  headroom,
  scrollsUpDown,
  onSettled,
  onEnd,
  sharedGl,
}: {
  // One GPU context with the other views of this name (see AssemblyOpts.sharedGl).
  sharedGl?: string;
  // Room kept clear at the top of the shot, for a name drawn over it (see AssemblyOpts.headroom).
  headroom?: number;
  // In a list that scrolls (see AssemblyOpts.scrollsUpDown).
  scrollsUpDown?: boolean;
  // CALLED ONCE THE PICTURE HAS LOADED OR FAILED to: the cover loads its picked colourway before
  // the other three (see TutorialSplash).
  onSettled?: () => void;
  // A SHADOW ON THE FLOOR under the product: the published tutorial's cover (see ClipStage).
  floorShadow?: boolean;
  clip: Clip | null;
  label?: string;
  // DRAWN OVER THE PICTURE, by whoever has something to say about it — the published page's
  // instruction. The caller's own node, placed by the caller inside this frame: it is the same
  // one it hangs on a screen that has no clip. A FUNCTION where `transport` is on: it is handed
  // the round transport (see RingTransport), to place in its own row.
  // `at`, where the clip is, 0 to 1: a caption saying the beat being played (see captionAt).
  caption?: React.ReactNode | ((transport: React.ReactNode, at: number) => React.ReactNode);
  // HOW BRIGHT THE PICTURE IS, where the page lets its reader choose (the published tutorial):
  // a dark wood under a set balanced for a pale seat is a silhouette with no grain in it.
  brightness?: number;
  // NO BUTTON IN THE CORNER, AND NO BIG VIEW BEHIND IT. For a frame that is already the page —
  // the published tutorial's, as wide as the screen it is on, where opening it "big" is the
  // same picture again in a box over itself. A thumbnail in a column still wants it.
  noBigView?: boolean;
  meshUrl?: (job: number) => string;
  // The frame's own shape — the two callers box it differently (a thumbnail beside an
  // instruction, a wide frame above one), and neither of them is this component's business.
  className?: string;
  // And how the same box looks with nothing in it, which is not the same styling: an empty
  // frame is the page's own placeholder and wears the page's own colours.
  emptyClassName?: string;
  autoPlay?: boolean;
  // PLAY ONCE AND STOP ON THE LAST FRAME — see ClipStage's own `once`.
  once?: boolean;
  // A FULL TRANSPORT UNDER THE PICTURE — play, both ends, a scrubber and where it is — instead
  // of the one play button floated over the corner.
  //
  // The corner button is right for a thumbnail in a list: the picture is small, it is one of
  // many, and the only question is whether it moves. It is wrong for the screen somebody is
  // ASSEMBLING FROM, where the clip is the instruction: the useful gesture there is to stop on
  // the frame where the washer meets the thread and look at it, which no button can do.
  //
  // The same bar the phase signs a fitting off with (see AssemblyPhase), in this page's own
  // colours: one control, learnt once.
  transport?: boolean;
  // A PICTURE THAT DOES NOT PLAY, and offers nothing to play it with — the product assembled, at
  // the end (see finishedClip). Still turned and zoomed like any other.
  still?: boolean;
  // A SPEED BUTTON BESIDE THE ROUND TRANSPORT, cycling through these (the published tutorial:
  // ×1 and ×2). Kept from one clip to the next: a reader who chose ×2 wants every screen at ×2.
  speeds?: number[];
  // AND SAYS SO WHEN A `once` CLIP REACHES ITS LAST FRAME: the assembly phase's "All" plays the
  // next sub-step's from there.
  onEnd?: () => void;
}) {
  const { t } = useLanguage();
  const [playing, setPlaying] = useState(autoPlay && !still);
  const [big, setBig] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  // WHERE THE CLIP IS, FOR THE BAR — and held here rather than in the stage because the bar is
  // drawn here. Rounded to the hundredth the readout prints: the loop reports every frame, and
  // a state write per frame that changes nothing visible is a React tree per frame.
  const [at, setAt] = useState(0);
  const report = useCallback((v: number) => {
    setAt((was) => (Math.abs(was - v) >= 0.005 || v === 0 || v === 1 ? v : was));
  }, []);
  // The frame a hand on the bar asked for, or null while nobody is asking. Null again on the
  // next play, so resuming is the loop's business and not a seek repeated for ever.
  const [seek, setSeek] = useState<number | null>(null);
  const goTo = (v: number) => { setPlaying(false); setSeek(v); setAt(v); };
  // PLAY, AND WHERE THE CLIP IS, round it (see RingTransport) — under the picture, or in the
  // caption's row where the caption takes it.
  const ring = (
    <RingAndSpeed
      at={at}
      playing={playing}
      onToggle={() => { setPlaying((was) => !was); setSeek(null); }}
      onSeek={goTo}
      speed={speed}
      onSpeed={speeds ? () => setSpeed((s) => nextSpeed(s, speeds)) : undefined}
    />
  );

  // A DIFFERENT CLIP IS A DIFFERENT QUESTION, so the transport goes back to how it opens.
  //
  // The player is not remounted between screens — the same component, a new clip — so without
  // this, pressing play on one screen would leave every screen after it playing on arrival,
  // and the bar would carry the last one's position until the first frame of the new one
  // reported in.
  useEffect(() => {
    setPlaying(autoPlay && !still);
    setAt(0);
    setSeek(null);
  }, [clip?.movement, autoPlay]);

  // A PRESS ON THE PICTURE IS PLAY AND PAUSE. It is where the hand already is, it is what a
  // video does everywhere else, and on a phone propped against a chair leg it is a target the
  // size of the frame rather than the size of a fingertip.
  //
  // A PRESS THAT MOVED IS NOT A CLICK, which is the whole of the care needed here: the clip
  // can be orbited, so a drag across the picture is somebody turning it to see the joint from
  // the other side and must not also stop it. Four pixels of slack, the same as the scene's
  // own click test, because a press on a trackpad is never perfectly still.
  const pressed = useRef<{ x: number; y: number } | null>(null);
  const tap = {
    onPointerDown: (e: React.PointerEvent) => { pressed.current = { x: e.clientX, y: e.clientY }; },
    onPointerUp: (e: React.PointerEvent) => {
      const from = pressed.current;
      pressed.current = null;
      if (!from) return;
      // The corner buttons live over the picture and say what they do; a press that lands on
      // one is that button's and not a press on the frame.
      if ((e.target as HTMLElement).closest('button, [role=slider]')) return;
      if (Math.abs(e.clientX - from.x) >= 4 || Math.abs(e.clientY - from.y) >= 4) return;
      // A still has nothing to play: a press on it is its caller's (the cover picks a colourway).
      if (still) return;
      setPlaying((was) => !was);
      setSeek(null);
    },
  };

  // Escape closes it, like every other dialog in here. Bound only while it is open, so a
  // page of thumbnails is not a page of keyboard listeners.
  useEffect(() => {
    if (!big) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBig(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big]);

  // THE TRANSPORT — play, START, the bar, END, and where it is. The same arrangement as the
  // phase's, because a person who has seen one of these has seen both, and the phase's is the
  // one that was designed against a movement being judged frame by frame.
  //
  // THE EXPANDED VIEW'S, in its own footer, where there used to be a single Play button and no
  // way to scrub — the one view big enough to judge a joint in, with the least control over it.
  // The public page's is round (see RingTransport).
  const transportBar = (wrap: string) => (
    <div className={wrap}>
      <button
        type="button"
        onClick={() => { setPlaying((was) => !was); setSeek(null); }}
        aria-label={playing ? t('notice.clip.pause') : t('notice.clip.play')}
        title={tip(playing ? t('notice.clip.pause') : t('notice.clip.play'))}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-notice-blue/40 text-notice-blue transition-colors hover:bg-notice-blue/5"
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      {/* THE TWO ENDS, NAMED AND PRESSABLE, exactly as the phase names them: which end of
          the bar is the part in the air and which is the part seated is the first thing
          anybody wants from this, and 0 against 1 is a convention rather than an answer. */}
      <button type="button" onClick={() => goTo(0)} className={publicEndBtn}>
        {t('notice.assembly.start')}
      </button>
      <input
        type="range"
        min={0}
        max={1}
        step={0.001}
        value={at}
        onChange={(e) => goTo(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-notice-blue/15 accent-notice-blue"
        aria-label={t('notice.assembly.scrub')}
      />
      <button type="button" onClick={() => goTo(1)} className={publicEndBtn}>
        {t('notice.assembly.end')}
      </button>
      <span className="w-10 shrink-0 text-right font-mono text-xs tabular-nums text-notice-blue/60">
        {`${((at * (clip ? clipMs(clip.movement) : CLIP_MS)) / CLIP_SPEED / 1000).toFixed(1)} s`}
      </span>
    </div>
  );

  // A CLIP THAT CANNOT BE DRAWN IS AN EMPTY FRAME, saying what the page already says for a
  // sub-step nobody has animated. Same for meshes that failed to arrive: what is owed is the
  // honest picture, not a spinner that never stops.
  if (!clip || state === 'failed') {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 ${emptyClassName ?? className ?? ''}`}>
        <VideoOff className="h-8 w-8" strokeWidth={1.25} />
        <span className="text-xs font-medium">{t('notice.public.noVideo')}</span>
      </div>
    );
  }

  return (
    <>
      <div className={`relative cursor-pointer overflow-hidden ${className ?? ''}`} {...tap}>
        {/* Paused while the big one is open: two canvases of the same clip is two downloads
            and two render loops, for a picture nobody is looking at. */}
        <ClipStage
          clip={clip}
          meshUrl={meshUrl}
          playing={playing && !big}
          onState={(st) => { setState(st); if (st !== 'loading') onSettled?.(); }}
          onT={transport ? report : undefined}
          seek={transport ? seek : null}
          once={once}
          brightness={brightness}
          speed={speed}
          floorShadow={floorShadow}
          headroom={headroom}
          scrollsUpDown={scrollsUpDown}
          sharedGl={sharedGl}
          // The transport goes back to "play" and the picture stays where it stopped.
          onEnd={() => { setPlaying(false); onEnd?.(); }}
        />
        {/* A SPINNER FOR A SCENE THAT IS BEING BUILT, and only then: `state` is only ever moved
            by the stage reporting in. */}
        {state === 'loading' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin opacity-60" />
          </div>
        )}
        <ReaderScale>{typeof caption === 'function' ? caption(transport ? ring : null, at) : caption}</ReaderScale>
        {/* The two controls, in the bottom corners, over the picture: pressed often enough to
            be worth a permanent home, small enough not to be the subject. The play button
            stands down where a full transport is drawn below — the same press, twice on one
            frame, and one of them over the picture it is about. */}
        {!transport && !still && (
        <button
          type="button"
          onClick={() => { setPlaying((was) => !was); setSeek(null); }}
          aria-label={playing ? t('notice.clip.pause') : t('notice.clip.play')}
          title={tip(playing ? t('notice.clip.pause') : t('notice.clip.play'))}
          className="absolute bottom-1.5 left-1.5 rounded-full bg-slate-900/60 p-1.5 text-white backdrop-blur transition-colors hover:bg-slate-900/80"
        >
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        </button>
        )}
        {!noBigView && (
        <button
          type="button"
          // Opening it is asking to watch, so it plays — including from a thumbnail somebody
          // had paused. The transport is shared from there on: pausing in the modal is a
          // paused thumbnail behind it, which is what closing it should reveal.
          onClick={() => { setPlaying(true); setBig(true); }}
          aria-label={t('notice.clip.expand')}
          title={tip(t('notice.clip.expand'))}
          className="absolute bottom-1.5 right-1.5 rounded-full bg-slate-900/60 p-1.5 text-white backdrop-blur transition-colors hover:bg-slate-900/80"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        )}
      </div>

      {/* THE TRANSPORT, UNDER THE PICTURE, where the caption does not place it (see `caption`).
          The bar across the page is the expanded view's, below, where a movement is judged
          frame by frame. */}
      {transport && typeof caption !== 'function' && (
        <div className="mt-3 flex w-full justify-center">{ring}</div>
      )}

      {/* SEEN BIG, which for a fitting is not a luxury: the whole question a clip answers is
          where two pieces meet, and in a thumbnail that is a few millimetres.
          A MODAL AND NOT A FULL-BLEED OVERLAY. It was the second — the picture edge to edge
          on a dark ground — and a 3D view with no frame around it has no edges of its own,
          so there was nothing on screen saying where the clip stopped and the page resumed,
          and nothing to aim at to leave. A panel, a backdrop you can click, a header and one
          control under it: the page is still there, dimmed, around a thing you can see the
          whole of.
          Its own stage rather than a scaled-up thumbnail — one canvas mounted where it is
          drawn is what keeps the small one from being resized under the hand that opened
          the big one. */}
      {big && (
        <div className={`${overlayCls} bg-black/70`} onClick={() => setBig(false)}>
          <div
            className={`flex w-full max-w-3xl flex-col overflow-hidden ${modalCardCls}`}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            {/* WHICH SCREEN THIS IS, centred — it titles the picture under it rather than
                labelling the corner it used to sit in. Centred by placing the close button
                over the row instead of beside it: with both in the flow, "centred" would be
                the middle of what is left after the button, which is visibly not the middle
                of the picture. */}
            <div className="relative flex items-center justify-center border-b border-slate-200 px-4 py-2.5">
              <span className="text-sm font-bold uppercase tracking-wide text-slate-600">
                {label ?? ''}
              </span>
              <button
                type="button"
                onClick={() => setBig(false)}
                aria-label={t('notice.clip.close')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* The picture, given the room the modal has. Sized in viewport height rather
                than by an aspect ratio: a clip is watched, and a frame that keeps its shape
                by shrinking on a laptop is the one place the extra width buys nothing.
                WHITE IN BOTH THEMES, and it is not an oversight that the dark one is not
                honoured here. The meshes are lit as product shots — a studio light, a room
                environment, wood and painted steel — and what that reads against is white.
                On a near-black ground the same picture is a dark object on a dark field with
                its own shadow for a background, which is the thing you cannot see. The panel
                around it still follows the theme; the picture is a picture. */}
            <div className="h-[60vh] cursor-pointer bg-white" {...tap}>
              <ClipStage
                clip={clip}
                meshUrl={meshUrl}
                playing={playing}
                onState={setState}
                // THE BAR UNDER IT IS THIS STAGE'S, so this is the one that reports where the
                // clip is and takes a hand on the scrubber. The small one behind the modal is
                // not playing (see its own `playing`), so the two never report at once.
                onT={report}
                seek={seek}
                once={once}
                speed={speed}
                onEnd={() => { setPlaying(false); onEnd?.(); }}
              />
            </div>
            {/* THE TRANSPORT, under the picture — the full one, not the single Play button
                that stood here. This is the view somebody opens to judge where two pieces
                actually meet, and it had the least control over the clip of anywhere in the
                app: no scrubbing, no way to hold it at the frame the joint closes.
                WHITE, like the picture above it and for the same reason written there. The
                bar is part of that panel rather than part of the page's chrome, and a themed
                strip under a white viewport reads as a seam. */}
            {transportBar('flex w-full items-center gap-3 border-t border-slate-200 bg-white px-4 py-3')}
          </div>
        </div>
      )}
    </>
  );
}
